import { type MouseEvent, type PropsWithChildren, type TouchEvent, useCallback } from 'react';

import { useSearchResultCardContext } from './SearchResultCardProvider';

import styles from './SearchResultCardImage.module.css';

const INTERACTIVE_TARGET_SELECTOR = 'a, button, input, select, textarea, [role="button"]';
const AUXILIARY_MOUSE_BUTTON = 1;
const SECONDARY_MOUSE_BUTTON = 2;

const isInteractiveEventTarget = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest(INTERACTIVE_TARGET_SELECTOR));

type SearchResultCardImageLinkWrapperProps = PropsWithChildren;

export const SearchResultCardImageLinkWrapper = ({ children }: SearchResultCardImageLinkWrapperProps) => {
  const { link, onClickPropertyCardLink, onContextMenuLinkOverlayActivate, onContextMenuLinkOverlayDeactivate } =
    useSearchResultCardContext();

  const openLinkInNewTab = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      onClickPropertyCardLink(e);
      window.open(link.href, '_blank', 'noopener,noreferrer');
    },
    [link.href, onClickPropertyCardLink],
  );

  const handleMouseDownCapture = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.button !== SECONDARY_MOUSE_BUTTON || isInteractiveEventTarget(e.target)) return;

      onContextMenuLinkOverlayActivate();
    },
    [onContextMenuLinkOverlayActivate],
  );

  const handleTouchStartCapture = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      if (isInteractiveEventTarget(e.target)) return;

      onContextMenuLinkOverlayActivate();
    },
    [onContextMenuLinkOverlayActivate],
  );

  const handleTouchInteractionEndCapture = useCallback(() => {
    onContextMenuLinkOverlayDeactivate();
  }, [onContextMenuLinkOverlayDeactivate]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.defaultPrevented || isInteractiveEventTarget(e.target) || (!e.metaKey && !e.ctrlKey)) return;

      e.preventDefault();
      e.stopPropagation();
      openLinkInNewTab(e);
    },
    [openLinkInNewTab],
  );

  const handleAuxClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.defaultPrevented || isInteractiveEventTarget(e.target) || e.button !== AUXILIARY_MOUSE_BUTTON) return;

      e.preventDefault();
      e.stopPropagation();
      openLinkInNewTab(e);
    },
    [openLinkInNewTab],
  );

  return (
    <div
      className={styles.imageLinkInteractionWrapper}
      onAuxClick={handleAuxClick}
      onClick={handleClick}
      onMouseDownCapture={handleMouseDownCapture}
      onTouchCancelCapture={handleTouchInteractionEndCapture}
      onTouchEndCapture={handleTouchInteractionEndCapture}
      onTouchStartCapture={handleTouchStartCapture}
    >
      {children}
    </div>
  );
};
